import React, {useState} from 'react';
import {
    Box,
    Typography,
    Button,
    Tooltip,
    Fab,
    Stack,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {useEffect, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import LanguageThemeBar from "../LanguageThemeBar";
import {useTranslation} from 'react-i18next';
import {useTheme} from '@mui/material/styles';
import useToolResults from './useToolResults';
import ResultTable from './ResultTable';
import ViewerContainer from '../PdfViewer/ViewerContainer/ViewerContainer';
import {alpha} from '@mui/material/styles';
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';
import * as XLSX from 'xlsx-js-style';
import VerticalResizeHandle from "../VerticalResizeHandle";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

// === BBox offset to apply before saving to the backend ===
// Adjust these values to your desired offset (in the same coordinate space as your backend expects).
const BBOX_OFFSET = {dx: 8, dy: 8};
const offsetBbox = (bbox, dx = BBOX_OFFSET.dx, dy = BBOX_OFFSET.dy) => {
    if (!Array.isArray(bbox) || bbox.length !== 4) return bbox;
    return [bbox[0] + dx, bbox[1] + dy, bbox[2] + dx, bbox[3] + dy];
};
const reverseOffsetBbox = (bbox) => offsetBbox(bbox, -BBOX_OFFSET.dx, -BBOX_OFFSET.dy);
const DRAW_BBOX_OFFSET = {dx: 10, dy: 10};
const displayBboxToPersistedBbox = (bbox) =>
    offsetBbox(bbox, BBOX_OFFSET.dx - DRAW_BBOX_OFFSET.dx, BBOX_OFFSET.dy - DRAW_BBOX_OFFSET.dy);


const norm = (s) =>
    (s ?? "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[：:]+$/g, "");

// Build a stable group id from a key_value_group annotation (used by the overlay
// to draw a dashed outline behind all member boxes)
const makeGroupIdFromAnnotation = (a) => {
    if (!a) return null;
    const pageNum = (a.page ?? a.source?.page ?? 1);
    const gb = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
    const suffix = Array.isArray(gb) ? gb.join('_') : 'nogb';
    return a.group_id || `${pageNum}_group_${a.group_label || 'group'}_${suffix}`;
};

const keyFromAnnotation = (a) => {
    if (!a || !a.type) return null;
    if (a.type === "key_value") {
        const ft = a.field_text;
        if (typeof ft === "string" && ft.trim()) return norm(ft);
        const f = a.field;
        if (typeof f === "string" && f.trim()) return norm(f);
        if (f && typeof f === "object") {
            const t = f.text || f.label || "";
            if (t && t.trim()) return norm(t);
        }
        return null;
    }
    if (a.type === "entity" || a.type === "summary_row") {
        const et = a.entity_type || a.label;
        if (typeof et === "string" && et.trim()) return norm(et);
        return null;
    }
    return null;
};

const getLeafLabels = (headers) => {
    if (!headers?.length) return [];
    const rows = headers.length;
    const cols = headers[0].length;
    const labels = [];
    for (let c = 1; c < cols; c++) {
        let label = "";
        for (let r = rows - 1; r >= 0; r--) {
            const v = headers[r][c];
            if (v && String(v).trim()) {
                label = String(v);
                break;
            }
        }
        if (!label) {
            for (let r = 0; r < rows; r++) {
                const v = headers[r][c];
                if (v && String(v).trim()) {
                    label = String(v);
                    break;
                }
            }
        }
        labels.push(label);
    }
    return labels;
};


export default function ToolResultsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const {files, toolId} = location.state || {};

    // If someone reloads and router state is lost, send them back safely.
    useEffect(() => {
        if (!files || !toolId) {
            // You can also show a toast here instead.
            navigate('/');
        }
    }, [files, toolId, navigate]);

    const {t} = useTranslation();
    const theme = useTheme();

    const {
        results,
        toolDetails,
        editedValues,
        setEditedValues,
        editingCell,
        setEditingCell,
        editValue,
        setEditValue,
    } = useToolResults(files, toolId);


    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedExecutionId, setSelectedExecutionId] = useState(null);
    const [selectedAnnotations, setSelectedAnnotations] = useState([]);
    const [viewerKey, setViewerKey] = useState(0); // Force viewer remount when annotations change


    const [peekKeyExternal, setPeekKeyExternal] = useState(null);
    const [peekNonce, setPeekNonce] = useState(0);
    const [hoveredKeyExternal, setHoveredKeyExternal] = useState(null);
    // 🔧 Redraw workflow state
    const [allowEdits, setAllowEdits] = useState(false);
    const [removeKeyExternal, setRemoveKeyExternal] = useState(null);
    const [removeNonce, setRemoveNonce] = useState(0);
    const [focusPageExternal, setFocusPageExternal] = useState(null);
    // store new boxes per execution + column index: overrides[execId][colIndex] = {page,bbox}
    const [overrides, setOverrides] = useState({});
    const [activeRedraw, setActiveRedraw] = useState(null); // { executionId, colIndex, annotationIds, removeKeys }
    const [extractingCell, setExtractingCell] = useState(null);
    const [redrawGroupId, setRedrawGroupId] = useState(null);


    const [viewerWidthPct, setViewerWidthPct] = useState(50);
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    // Track which cell is being processed during drag-and-drop
    const [processingCell, setProcessingCell] = useState(null); // { executionId, colIndex }

    const makeEntityKey = (page, bbox) => `${page}_entity_${bbox.join('_')}`;

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deletedIds, setDeletedIds] = useState(new Set());
    const toggleRowSelected = useCallback((executionId, on) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (on) next.add(executionId); else next.delete(executionId);
            return next;
        });
    }, []);
    const clearSelection = () => setSelectedIds(new Set());

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Export menu state
    const [exportMenuPosition, setExportMenuPosition] = useState(null);
    const openExportMenu = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setExportMenuPosition({
            top: rect.bottom + 2, // Position menu's top at the button's bottom + 2px margin
            left: rect.left,      // Position menu's left at the button's left
        });
    };
    const closeExportMenu = () => setExportMenuPosition(null);

    // Build pairs (file+result), hide deleted, then slice for current page
    const alivePairs = (results || []).map((r, i) => ({res: r, file: files?.[i]}))
        .filter(p => !deletedIds?.has?.(p.res?.data?.execution_id));
    const totalRows = alivePairs.length;
    const start = (page - 1) * pageSize;
    const visiblePairs = alivePairs.slice(start, start + pageSize);
    const hasPrev = page > 1;
    const hasNext = start + pageSize < totalRows;

    // Open the Material UI dialog from the bulk action; actual deletion happens in handleConfirmDelete
    const handleConfirmDelete = async () => {
        if (selectedIds.size === 0) {
            setDeleteOpen(false);
            return;
        }
        setDeleting(true);

        try {
            const resp = await apiFetch(`${API_BASE}/delete_executions`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ids: Array.from(selectedIds)}),
            });
            if (!resp.ok) {
                let msg = resp.statusText || resp.status;
                try {
                    const err = await resp.json();
                    msg = err.error || msg;
                } catch {
                }
                alert(`${t('delete_failed') || 'Delete failed'}: ${msg}`);
                return;
            }
            // Hide the deleted rows immediately
            setDeletedIds(prev => {
                const next = new Set(prev);
                Array.from(selectedIds).forEach(id => next.add(id));
                return next;
            });
            clearSelection();
            setDeleteOpen(false);

        } catch (e) {
            console.error(e);
            alert(t('delete_failed') || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    // Compact bulk actions (shown only when rows are selected)
    const bulkActions = selectedIds.size > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption">
                {selectedIds.size} {t('pagination.rows') || 'rows'} {t('selected') || 'selected'}
            </Typography>
            <Tooltip title={t('deleteSelected') || 'Delete selected'}>
                <IconButton
                    color="error"
                    size="small"
                    onClick={() => setDeleteOpen(true)}
                > <DeleteOutlineRoundedIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Stack>
    ) : null;

    const triggerPeek = (page, bbox) => {
        setPeekKeyExternal(makeEntityKey(page, bbox));
        setPeekNonce(n => n + 1);   // 🔔 always changes, so effect re-fires
    };

    // Build all remove-keys for a given cell (e.g., both field+value for key_value)
    const computeRemoveKeysForCell = (executionId, colIndex) => {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return [];
        // Rebuild headers like the table (prepends "Filename" column)
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) row.unshift("Filename"); else row.unshift("");
        });
        const leafHeaders = getLeafLabels(headers);
        const label = leafHeaders[colIndex];
        const targetKey = norm(label);
        const keys = new Set();
        const add = (page, bbox) => {
            if (!Array.isArray(bbox) || bbox.length !== 4) return;
            const pageNum = page ?? 1;
            keys.add(makeEntityKey(pageNum, bbox));
            keys.add(makeEntityKey(pageNum, reverseOffsetBbox(bbox)));
        };
        for (const a of (res.data.annotations || [])) {
            const k = keyFromAnnotation(a);
            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                const page = a.page ?? a.source?.page ?? 1;
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                        const vb = item?.value?.bbox;
                        if (Array.isArray(vb) && vb.length === 4) add(page, vb);
                    }
                }
                continue;
            }
            if (k && (k === targetKey || k.includes(targetKey))) {
                const page = a.value?.page ?? a.field?.page ?? a.page ?? 1;
                if (a.type === "key_value") {
                    if (a.field?.bbox) add(page, a.field.bbox);
                    if (a.value?.bbox) add(page, a.value.bbox);
                } else if (a.bbox) {
                    add(page, a.bbox);
                }
            }
        }
        return Array.from(keys);
    };

    const computeAnnotationIdsForCell = (executionId, colIndex) => {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return [];
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) row.unshift("Filename"); else row.unshift("");
        });
        const leafHeaders = getLeafLabels(headers);
        const targetKey = norm(leafHeaders[colIndex]);
        const ids = new Set();

        for (const a of (res.data.annotations || [])) {
            const k = keyFromAnnotation(a);
            if (k && (k === targetKey || k.includes(targetKey)) && a.id) {
                ids.add(a.id);
                continue;
            }
            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey)) && a.id) {
                        ids.add(a.id);
                        break;
                    }
                }
            }
        }
        return Array.from(ids);
    };

    const FORCE_DRAW = "__force_draw__";   // sentinel: remove nothing, but still enter draw mode

    // 🔧 Called when user clicks the “Redraw” control on a cell
    const handleCellRedraw = ({executionId, page, bbox, fileObj, colIndex}) => {

        // Compute the groupId for this cell (if it belongs to a key_value_group)
        const computeGroupIdForCell = (execId, colIx) => {
            const res = results.find(r => r?.data?.execution_id === execId);
            if (!res?.data?.annotations || !toolDetails?.headerConfig) return null;
            // rebuild the headers like the table (prepend "Filename")
            const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
            headers.forEach((row, idx) => {
                if (idx === 0) row.unshift("Filename"); else row.unshift("");
            });
            const leafHeaders = getLeafLabels(headers);
            const targetKey = norm(leafHeaders[colIx]);
            for (const a of (res.data.annotations || [])) {
                if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                    const pageNum = a.page ?? a.source?.page ?? 1;
                    const groupBox = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
                    const gid = a.group_id || `${pageNum}_group_${a.group_label || "group"}_${Array.isArray(groupBox) ? groupBox.join('_') : 'nogb'}`;
                    for (const it of a.items) {
                        const itKey = norm((it?.field?.text) ?? (typeof it?.field === "string" ? it.field : ""));
                        if (itKey && (itKey === targetKey || itKey.includes(targetKey))) return gid;
                    }
                }
            }
            return null;
        };

        // remember it so the viewer can tag the next drawn box
        setRedrawGroupId(computeGroupIdForCell(executionId, colIndex));

        const switchingExec = !selectedFile || executionId !== selectedExecutionId;
        if (switchingExec) {
            setSelectedFile(fileObj);
            setSelectedExecutionId(executionId);
            const res = results.find(r => r?.data?.execution_id === executionId);
            const anns = res?.data?.annotations || [];
            // Ensure all annotations have IDs
            anns.forEach((a, idx) => {
                if (!a.id) {
                    a.id = `ann_${executionId}_${idx}`;
                }
            });
            const flat = [];
            (anns || []).forEach(a => {
                const pageNum = (a.page ?? 1);
                const push = (bbox, extra = {}) => {
                    if (Array.isArray(bbox) && bbox.length === 4) {
                        const uid = `${pageNum}_entity_${bbox.join('_')}`;
                        flat.push({page: pageNum, bbox, type: 'entity', __uid: uid, annotationId: a.id, ...extra});
                    }
                };
                if (a.type === "key_value") {
                    if (a.field?.bbox) push(a.field.bbox, {label: a.field?.text ?? ""});
                    if (a.value?.bbox) push(a.value.bbox, {label: a.value?.text ?? ""});
                } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                    const pageNum2 = a.page ?? a.source?.page ?? pageNum;
                    const groupBox = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
                    const groupId = a.group_id || `${pageNum2}_group_${a.group_label || "group"}_${Array.isArray(groupBox) ? groupBox.join('_') : 'nogb'}`;
                    for (const item of a.items) {
                        const vb = item?.value?.bbox;
                        if (Array.isArray(vb) && vb.length === 4) {
                            const uid2 = `${pageNum2}_entity_${vb.join('_')}`;
                            flat.push({
                                page: pageNum2,
                                bbox: vb,
                                type: 'entity',
                                __uid: uid2,
                                label: item?.field?.text ?? "",
                                groupId,
                                annotationId: a.id
                            });
                        }
                    }
                } else if (a.type === "entity" || a.type === "summary_row") {
                    // Use entity_type first (column header) for matching, fallback to label
                    if (a.bbox) push(a.bbox, {
                        label: a.entity_type ?? a.label ?? "",
                        entity_type: a.entity_type ?? a.label ?? ""
                    });
                } else if (a.type === "table_row" && Array.isArray(a.columns)) {
                    a.columns.forEach((col, i) => col?.bbox && push(col.bbox, {colIndex: i, label: col.text ?? ""}));
                }
                // Removed catch-all else clause that was creating unwanted extra boxes
            });
            setSelectedAnnotations(flat);
            // let viewer mount highlights, then kick off remove+drawing
            setTimeout(() => {
                const baseKeys = computeRemoveKeysForCell(executionId, colIndex);
                // also nuke the last locally-drawn override (handles rounding/key mismatches)
                const merged = new Set(baseKeys);
                const ov = overrides?.[executionId]?.[colIndex];
                if (ov?.bbox) merged.add(makeEntityKey(ov.page ?? 1, ov.bbox));
                const removeKey = merged.size
                    ? Array.from(merged)
                    : (page && bbox ? makeEntityKey(page, bbox) : FORCE_DRAW);
                setRemoveKeyExternal(removeKey);
                setRemoveNonce(n => n + 1);
                setFocusPageExternal(page || 1);
                setAllowEdits(true);
                setActiveRedraw({
                    executionId,
                    colIndex,
                    annotationIds: computeAnnotationIdsForCell(executionId, colIndex),
                    removeKeys: Array.isArray(removeKey) ? removeKey : [removeKey],
                });
            }, 0);
        } else {
            const baseKeys = computeRemoveKeysForCell(executionId, colIndex);
            const merged = new Set(baseKeys);
            const ov = overrides?.[executionId]?.[colIndex];
            if (ov?.bbox) merged.add(makeEntityKey(ov.page ?? 1, ov.bbox));
            const removeKey = merged.size
                ? Array.from(merged)
                : (page && bbox ? makeEntityKey(page, bbox) : FORCE_DRAW);
            setRemoveKeyExternal(removeKey);
            setRemoveNonce(n => n + 1);
            setFocusPageExternal(page || 1);
            setAllowEdits(true);
            setActiveRedraw({
                executionId,
                colIndex,
                annotationIds: computeAnnotationIdsForCell(executionId, colIndex),
                removeKeys: Array.isArray(removeKey) ? removeKey : [removeKey],
            });
        }
    };

    // ↓ place near your other helpers in ToolResultsPage.js
    async function applyOverrideAndPersist({executionId, colIndex, page, bbox, text = null, annotationIds = []}) {
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations || !toolDetails?.headerConfig) return;

        // Save bbox as-is, without offset

        // Build the same leaf header labels the table uses (remember you prepend "Filename")
        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) row.unshift("Filename"); else row.unshift("");
        });
        const leafHeaders = getLeafLabels(headers);     // existing util
        const targetKey = norm(leafHeaders[colIndex]);  // existing util

        // Work on a deep copy so we don't mutate by reference
        const cloned = JSON.parse(JSON.stringify(res.data.annotations || []));

        // Ensure all annotations have stable IDs
        cloned.forEach((a, idx) => {
            if (!a.id) {
                // Generate a stable ID based on type and index
                a.id = `ann_${a.type}_${idx}_${Date.now()}`;
            }
        });

        let updated = false;
        let matchedAnnotationId = null;
        console.log('[applyOverrideAndPersist] Looking for annotation with targetKey:', targetKey);
        const targetAnnotationIds = new Set(annotationIds.filter(Boolean));
        for (const a of cloned) {
            const k = keyFromAnnotation(a);  // existing util (key_value/entity/summary_row)
            console.log('[applyOverrideAndPersist] Checking annotation:', {type: a.type, key: k, id: a.id});
            const matchesById = a.id && targetAnnotationIds.has(a.id);
            const matchesByKey = k && (k === targetKey || k.includes(targetKey));
            const matchesGroupItem = a?.type === "key_value_group" && Array.isArray(a.items) && a.items.some((item) => {
                const itemKey = norm(
                    (item?.field?.text) ??
                    (typeof item?.field === "string" ? item.field : "")
                );
                return itemKey && (itemKey === targetKey || itemKey.includes(targetKey));
            });

            if (matchesById || matchesByKey || matchesGroupItem) {
                console.log('[applyOverrideAndPersist] MATCH FOUND! Updating annotation:', a.id);
                matchedAnnotationId = a.id;
                // 🔧 your chosen behavior: keep only ONE rectangle (for the value)
                if (a.type === "key_value") {
                    if (!a.value) a.value = {};
                    a.value.page = page ?? a.value.page ?? a.page ?? 1;
                    a.value.bbox = bbox;
                    if (text !== null) a.value.text = text;
                    // remove key bbox
                    if (a.field) {
                        delete a.field.bbox;
                        // keep field text/label intact
                    }
                } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                    const pageNum = page ?? a.page ?? a.source?.page ?? 1;
                    // find the matching item by field text
                    let matched = false;
                    for (const item of a.items) {
                        const itemKey = norm(
                            (item?.field?.text) ??
                            (typeof item?.field === "string" ? item.field : "")
                        );
                        if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                            if (!item.value) item.value = {};
                            item.value.page = pageNum;
                            item.value.bbox = bbox;
                            if (text !== null) item.value.text = text;
                            matched = true;
                            break;
                        }
                    }
                    if (!matched && a.items.length) {
                        // fallback: attach to the first item
                        const val = {page: pageNum, bbox: bbox};
                        if (text !== null) val.text = text;
                        a.items[0].value = val;
                    }
                } else if (a.type === "entity" || a.type === "summary_row") {
                    a.page = page ?? a.page ?? 1;
                    a.bbox = bbox;
                    if (text !== null) a.text = text;
                    // Preserve the entity_type so we can match columns after drag-and-drop
                    if (!a.entity_type) {
                        a.entity_type = targetKey;
                    }
                }
                updated = true;
                break; // stop at first match
            }
        }

        // If the field never existed (the "—" case), create it as a simple entity.
        if (!updated) {
            console.log('[applyOverrideAndPersist] No match found, creating new annotation');
            const label = leafHeaders[colIndex] ?? "";  // human-visible column label
            const newId = `ann_entity_${cloned.length}_${Date.now()}`;
            cloned.push({
                id: newId,
                type: "entity",
                label,
                entity_type: targetKey, // Store the column header for matching after drag-and-drop
                text: text ?? "",            // use extracted text if available
                page: page ?? 1,
                bbox: bbox
            });
            matchedAnnotationId = newId;
            updated = true;
        }

        // Persist using the same endpoint as text edits
        try {
            const payload = {result_json: cloned};

            const resp = await apiFetch(`${API_BASE}/update_execution/${executionId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                let msg = resp.statusText || resp.status;
                try {
                    const err = await resp.json();
                    msg = err.error || msg;
                } catch {
                    // non-JSON error body
                }
                console.error('Failed to save new bbox:', msg);
                alert(`Failed to save annotation change: ${msg}`);
                return matchedAnnotationId; // Still return the ID even if save fails
            } else {
                // ✅ Keep local state aligned with what we just saved
                if (res?.data) {
                    res.data.annotations = cloned;
                }
            }

            // Optional: keep local copy in sync so future interactions see the new box
            res.data.annotations = cloned;
        } catch (e) {
            console.error('Network error while saving new bbox:', e);
            alert('Error saving annotation change.');
            // Return the ID even if network error occurs
            console.log('[applyOverrideAndPersist] Network error, but returning annotation ID anyway:', matchedAnnotationId);
            return matchedAnnotationId;
        }

        // Return the annotation ID that was created/updated
        console.log('[applyOverrideAndPersist] Returning annotation ID:', matchedAnnotationId);
        return matchedAnnotationId;
    }


    // 🔧 When viewer reports a new rectangle drawn, capture it and lock viewer again
    const handleAnnotationAdded = async (newAnn) => {
        if (!activeRedraw) return;
        const {executionId, colIndex, annotationIds = [], removeKeys = []} = activeRedraw;
        const {page, bbox, pageImage, bboxNorm, __raw_bbox} = newAnn || {};

        // Prefer the backend-safe bbox (no UI offsets) when persisting/calling APIs
        const bboxRaw = (Array.isArray(__raw_bbox) && __raw_bbox.length === 4)
            ? __raw_bbox
            : bbox;
        // Apply the offset before saving to database
        const bboxForBackend = offsetBbox(bboxRaw);
        try {
            if (page && Array.isArray(bbox) && bbox.length === 4) {
                // determine groupId (if this cell belongs to a group) so the dashed outline updates
                let overrideGroupId = null;
                try {
                    const resLocal = results.find(r => r?.data?.execution_id === executionId);
                    const annsLocal = resLocal?.data?.annotations || [];
                    if (toolDetails?.headerConfig) {
                        const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
                        headers.forEach((row, idx) => {
                            if (idx === 0) row.unshift("Filename"); else row.unshift("");
                        });
                        const leafHeaders = getLeafLabels(headers);
                        const targetKey = norm(leafHeaders[colIndex]);
                        for (const a of annsLocal) {
                            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                                for (const it of a.items) {
                                    const itKey = norm((it?.field?.text) ?? (typeof it?.field === "string" ? it.field : ""));
                                    if (itKey && (itKey === targetKey || itKey.includes(targetKey))) {
                                        overrideGroupId = makeGroupIdFromAnnotation(a);
                                        break;
                                    }
                                }
                                if (overrideGroupId) break;
                            }
                        }
                    }
                } catch {
                }

                // Fallback to the group we computed when entering redraw
                if (!overrideGroupId && redrawGroupId) overrideGroupId = redrawGroupId;

                // 💾 persist to backend using the raw (non-offset) bbox and get the annotation ID
                const dbAnnotationId = await applyOverrideAndPersist({
                    executionId,
                    colIndex,
                    page,
                    bbox: bboxForBackend,
                    annotationIds,
                }) || `new_${executionId}_${colIndex}`;
                console.log('[handleAnnotationAdded] Got annotation ID from persist:', dbAnnotationId);
                console.log('[handleAnnotationAdded] dbAnnotationId type:', typeof dbAnnotationId);
                console.log('[handleAnnotationAdded] dbAnnotationId value:', JSON.stringify(dbAnnotationId));

                setOverrides(prev => ({
                    ...prev,
                    [executionId]: {
                        ...(prev[executionId] || {}),
                        [colIndex]: {page, bbox, ...(overrideGroupId ? {groupId: overrideGroupId} : {})}
                    }
                }));

                const newBoxUid = makeEntityKey(page, bbox);
                console.log('[handleAnnotationAdded] Setting annotation with ID:', dbAnnotationId);

                // Update annotations - handle both cases: annotation added by viewer, or needs to be added here
                setSelectedAnnotations(prev => {
                    console.log('[handleAnnotationAdded] Current annotations before update:', prev.map(a => ({uid: a.__uid, id: a.annotationId})));

                    const removeKeySet = new Set(removeKeys.filter(k => k && k !== FORCE_DRAW));
                    const targetAnnotationIds = new Set([
                        ...annotationIds.filter(Boolean),
                        dbAnnotationId,
                    ]);
                    const withoutOldBoxes = prev.filter((ann) => {
                        if (ann.__uid && removeKeySet.has(ann.__uid) && ann.__uid !== newBoxUid) return false;
                        if (!ann.groupId && ann.annotationId && targetAnnotationIds.has(ann.annotationId) && ann.__uid !== newBoxUid) return false;
                        return true;
                    });

                    // Find annotation by UID (may already exist without ID if viewer added it)
                    const existingIndex = withoutOldBoxes.findIndex(ann => ann.__uid === newBoxUid);

                    if (existingIndex >= 0) {
                        // Update existing box with the ID
                        const updated = withoutOldBoxes.map((ann, idx) =>
                            idx === existingIndex
                                ? {...ann, bbox, page, annotationId: dbAnnotationId, ...(overrideGroupId ? {groupId: overrideGroupId} : {})}
                                : ann
                        );
                        console.log('[handleAnnotationAdded] Updated existing annotation at index', existingIndex, ':', updated[existingIndex]);
                        return updated;
                    } else {
                        // Add new box
                        const newAnn = {
                            page,
                            bbox,
                            type: 'entity',
                            __uid: newBoxUid,
                            annotationId: dbAnnotationId,
                            ...(overrideGroupId ? {groupId: overrideGroupId} : {})
                        };
                        console.log('[handleAnnotationAdded] Adding new annotation:', newAnn);
                        return [...withoutOldBoxes, newAnn];
                    }
                });

                // CRITICAL: Force another state update after a brief delay to ensure the ID is set
                // This handles race conditions where the drag starts before React finishes the state update
                setTimeout(() => {
                    setSelectedAnnotations(prev => {
                        return prev.map(ann => {
                            if (ann.__uid === newBoxUid && !ann.annotationId) {
                                console.log('[handleAnnotationAdded] DELAYED: Setting missing annotationId on', ann.__uid);
                                return {...ann, annotationId: dbAnnotationId};
                            }
                            return ann;
                        });
                    });
                }, 50);

                // visually wiggle the new box and return to read-only
                setTimeout(() => {
                    setPeekKeyExternal(makeEntityKey(page, bbox));
                    setPeekNonce(n => n + 1);
                }, 100);

                // 🧠 NEW: if we captured page image + bboxNorm, ask backend for text and auto-fill the cell
                if (pageImage && bboxNorm && Array.isArray(bboxNorm) && bboxNorm.length === 4) {
                    setExtractingCell({executionId, colIndex});
                    try {
                        const resp = await apiFetch(`${API_BASE}/extract-snippet-text`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({
                                page_image: pageImage,
                                bbox_norm: bboxNorm,
                                // provider: 'openai',   // or keep in component state if you let the user pick
                                // model: 'gpt-4.1',
                            }),
                        });

                        let data = {};
                        try {
                            data = await resp.json();
                        } catch {
                            // non-JSON response → leave data as {}
                        }

                        const newText = (data && data.text) || "";

                        if (typeof newText === "string") {
                            // Write the extracted text into the correct cell and persist
                            const res = results.find(r => r?.data?.execution_id === executionId);
                            if (res?.data?.annotations && toolDetails?.headerConfig) {
                                const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
                                headers.forEach((row, idx) => {
                                    if (idx === 0) row.unshift("Filename"); else row.unshift("");
                                });
                                const leaf = getLeafLabels(headers);
                                const targetKey = norm(leaf[colIndex]);
                                const cloned = JSON.parse(JSON.stringify(res.data.annotations || []));

                                let updated = false;
                                for (const a of cloned) {
                                    const k = keyFromAnnotation(a);
                                    if (!k) continue;
                                    if (k === targetKey || k.includes(targetKey)) {
                                        if (a.type === "key_value") {
                                            if (!a.value) a.value = {};
                                            a.value.text = newText;
                                        } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                                            for (const item of a.items) {
                                                const itemKey = norm(
                                                    (item?.field?.text) ??
                                                    (typeof item?.field === "string" ? item.field : "")
                                                );
                                                if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                                                    if (!item.value) item.value = {};
                                                    item.value.text = newText;
                                                    break;
                                                }
                                            }
                                        } else if (a.type === "entity" || a.type === "summary_row") {
                                            a.text = newText;
                                        }
                                        updated = true;
                                        break;
                                    }

                                }
                                if (!updated) {
                                    const label = leaf[colIndex] ?? "";
                                    cloned.push({type: "entity", label, text: newText, page: page ?? 1, bbox});
                                }

                                // persist updated text
                                const saveResp = await apiFetch(`${API_BASE}/update_execution/${executionId}`, {
                                    method: 'PUT',
                                    headers: {'Content-Type': 'application/json'},
                                    body: JSON.stringify({result_json: cloned}),
                                });

                                if (saveResp.ok) {
                                    res.data.annotations = cloned; // keep local in sync
                                    setEditedValues(prev => {
                                        const base = prev[executionId] ?? Array(leaf.length).fill(undefined);
                                        const next = [...base];
                                        next[colIndex] = newText;
                                        return {...prev, [executionId]: next};
                                    });
                                }
                            }
                        }
                    } catch (e) {
                        console.warn("extract-snippet-text failed", e);

                    }

                }
            }
        } finally {
            setExtractingCell(null);
            setAllowEdits(false);
            setActiveRedraw(null);
            setRemoveKeyExternal(null);
            setFocusPageExternal(null);
            setRedrawGroupId(null);
        }
    };

    // Handler for when an annotation is moved via drag-and-drop
    const handleAnnotationMoved = async (movedData) => {
        const {annotation, bbox, page, oldAnnotation, key, pageImage, bboxNorm} = movedData || {};

        console.log('[handleAnnotationMoved] Received:', {annotation, bbox, page, key, hasPageImage: !!pageImage, hasBboxNorm: !!bboxNorm});

        if (!annotation || !bbox || !page) return;
        if (!selectedExecutionId) return; // Must have an active execution

        // Prevent concurrent processing - if already processing, ignore this call
        if (processingCell) {
            console.log('[handleAnnotationMoved] Already processing, ignoring duplicate call');
            return;
        }

        const executionId = selectedExecutionId;

        console.log('[handleAnnotationMoved] Processing with context:', {
            executionId,
            selectedFileName: selectedFile?.name,
            annotationPage: page,
            toolId
        });

        // Find which column this annotation represents by matching label/entity_type from the DATABASE
        // Don't rely on selectedAnnotations state - go directly to the source of truth
        const res = results.find(r => r?.data?.execution_id === executionId);
        if (!res?.data?.annotations) {
            console.log('[handleAnnotationMoved] No annotations found in results');
            return;
        }

        const headers = JSON.parse(JSON.stringify(toolDetails?.headerConfig || []));
        headers.forEach((row, idx) => {
            if (idx === 0) row.unshift("Filename"); else row.unshift("");
        });
        const leafHeaders = getLeafLabels(headers);

        const movedAnnotationId = annotation?.annotationId || annotation?.id || oldAnnotation?.annotationId || oldAnnotation?.id;
        let bestMatch = movedAnnotationId
            ? res.data.annotations.find(ann => ann?.id === movedAnnotationId)
            : null;
        let minDistance = Infinity;

        // Fallback: find the annotation whose bbox is closest to oldAnnotation.bbox.
        for (const ann of res.data.annotations) {
            if (bestMatch) break;
            // Get the annotation's current bbox (accounting for different types)
            let annBbox = null;
            let annPage = ann.page || 1;

            if (ann.type === 'key_value' && ann.value?.bbox) {
                annBbox = ann.value.bbox;
                annPage = ann.value.page || ann.page || 1;
            } else if (ann.type === 'entity' || ann.type === 'summary_row') {
                annBbox = ann.bbox;
            }

            if (!annBbox || annPage !== page) continue;

            // Apply reverse offset to match display coordinates
            const displayBbox = reverseOffsetBbox(annBbox);

            // Calculate distance to oldAnnotation.bbox if available, otherwise use key's bbox
            const referenceBbox = oldAnnotation?.bbox || (key ? key.split('_').slice(2).map(Number) : null);
            if (!referenceBbox || referenceBbox.length !== 4) continue;

            const distance = Math.sqrt(
                Math.pow(displayBbox[0] - referenceBbox[0], 2) +
                Math.pow(displayBbox[1] - referenceBbox[1], 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                bestMatch = ann;
            }
        }

        if (!bestMatch) {
            console.log('[handleAnnotationMoved] Could not find matching annotation in database');
            return;
        }

        console.log('[handleAnnotationMoved] Found best match annotation:', bestMatch.id, 'distance:', minDistance);

        // Now find which column this annotation belongs to
        const annKey = keyFromAnnotation(bestMatch) || norm(annotation?.entity_type ?? annotation?.label ?? "");
        if (!annKey) {
            console.log('[handleAnnotationMoved] No key found for annotation');
            return;
        }

        let colIndex = -1;
        for (let c = 0; c < leafHeaders.length; c++) {
            if (norm(leafHeaders[c]) === annKey || norm(leafHeaders[c]).includes(annKey)) {
                colIndex = c;
                break;
            }
        }

        console.log('[handleAnnotationMoved] Found colIndex:', colIndex, 'for key:', annKey);

        if (colIndex >= 0) {
            try {
                // Set processing state for this cell
                setProcessingCell({executionId, colIndex});

                let extractedText = '';

                // If we have pageImage and bboxNorm, extract text from the new position
                if (pageImage && bboxNorm && Array.isArray(bboxNorm) && bboxNorm.length === 4) {
                    console.log('[handleAnnotationMoved] Extracting text from new position');
                    try {
                        const resp = await apiFetch(`${API_BASE}/extract-snippet-text`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({
                                page_image: pageImage,
                                bbox_norm: bboxNorm,
                            }),
                        });

                        let data = {};
                        try {
                            data = await resp.json();
                        } catch {}

                        extractedText = (data && data.text) || "";
                        console.log('[handleAnnotationMoved] Extracted text from API:', extractedText);
                    } catch (e) {
                        console.warn("extract-snippet-text failed", e);
                    }
                }

                // Update the annotation with the new bbox and text, then persist to database
                // Apply offset before saving to database (consistent with how bboxes are stored)
                const bboxForDb = displayBboxToPersistedBbox(bbox);
                await applyOverrideAndPersist({
                    executionId,
                    colIndex,
                    page,
                    bbox: bboxForDb,
                    text: extractedText,
                    annotationIds: [bestMatch.id].filter(Boolean),
                });

                // Update local UI state
                setOverrides(prev => ({
                    ...prev,
                    [executionId]: {
                        ...(prev[executionId] || {}),
                        [colIndex]: {page, bbox}
                    }
                }));

                // Update the table cell with the new extracted text
                if (extractedText) {
                    setEditedValues(prev => {
                        const currentRow = prev[executionId] || [];
                        const updatedRow = [...currentRow];
                        updatedRow[colIndex] = extractedText;
                        console.log('[handleAnnotationMoved] Setting edited value:', {executionId, colIndex, value: extractedText});
                        return {...prev, [executionId]: updatedRow};
                    });
                }

                // Update the moved annotation in place without reloading all annotations
                // This preserves zoom level and prevents other boxes from shifting
                const newUid = makeEntityKey(page, bbox);

                // Update the annotation in selectedAnnotations to reflect the new position
                setSelectedAnnotations(prev => {
                    const updated = prev.map(ann => {
                        // Match by annotation ID (most reliable)
                        if (ann.annotationId === bestMatch.id) {
                            const updatedAnn = {
                                ...ann,
                                bbox: bbox,
                                page: page,
                                __uid: newUid  // Update the UID to reflect new position
                            };
                            console.log('[handleAnnotationMoved] Updated annotation by ID:', updatedAnn);
                            return updatedAnn;
                        }
                        // Fallback: match by old bbox
                        if (oldAnnotation && JSON.stringify(ann.bbox) === JSON.stringify(oldAnnotation.bbox)) {
                            const updatedAnn = {
                                ...ann,
                                bbox: bbox,
                                page: page,
                                __uid: newUid,
                                annotationId: bestMatch.id // Add the ID if it was missing
                            };
                            console.log('[handleAnnotationMoved] Updated annotation by bbox:', updatedAnn);
                            return updatedAnn;
                        }
                        return ann;
                    });
                    console.log('[handleAnnotationMoved] All annotations after update:', updated);
                    return updated;
                });
            } finally {
                // Clear processing state after all operations complete
                setProcessingCell(null);
            }
        }
    };

    const handleTableCellClick = ({executionId, page, bbox, fileObj}) => {
        // 1) Ensure the correct file/viewer is open
        const switchingExec = !selectedFile || executionId !== selectedExecutionId;
        if (switchingExec) {
            setSelectedFile(fileObj);
            setSelectedExecutionId(executionId);

            // 2) Seed highlights for this execution (same flattener as onFilenameClick)
            const res = results.find(r => r?.data?.execution_id === executionId);
            const anns = res?.data?.annotations || [];

            // Ensure all annotations have IDs
            anns.forEach((a, idx) => {
                if (!a.id) {
                    a.id = `ann_${executionId}_${idx}`;
                }
            });

            const flat = [];
            (anns || []).forEach(a => {
                const pageNum = (a.page ?? 1);
                const push = (bbox, extra = {}) => {
                    if (Array.isArray(bbox) && bbox.length === 4) {
                        const uid = `${pageNum}_entity_${bbox.join('_')}`;
                        flat.push({page: pageNum, bbox, type: 'entity', __uid: uid, annotationId: a.id, ...extra});
                    }
                };
                if (a.type === "key_value") {
                    if (a.field?.bbox) push(a.field.bbox, {label: a.field?.text ?? ""});
                    if (a.value?.bbox) push(a.value.bbox, {label: a.value?.text ?? ""});
                } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                    const pageNum2 = a.page ?? a.source?.page ?? page;
                    const groupBox = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
                    const groupId = a.group_id || `${pageNum2}_group_${a.group_label || "group"}_${Array.isArray(groupBox) ? groupBox.join('_') : 'nogb'}`;
                    for (const item of a.items) {
                        const vb = item?.value?.bbox;
                        if (Array.isArray(vb) && vb.length === 4) {
                            const uid2 = `${pageNum2}_entity_${vb.join('_')}`;
                            flat.push({
                                page: pageNum2,
                                bbox: vb,
                                type: 'entity',
                                __uid: uid2,
                                label: item?.field?.text ?? "",
                                groupId,
                                annotationId: a.id
                            });
                        }
                    }
                } else if (a.type === "entity" || a.type === "summary_row") {
                    // Use entity_type first (column header) for matching, fallback to label
                    if (a.bbox) push(a.bbox, {
                        label: a.entity_type ?? a.label ?? "",
                        entity_type: a.entity_type ?? a.label ?? ""
                    });
                } else if (a.type === "table_row" && Array.isArray(a.columns)) {
                    a.columns.forEach((col, i) => col?.bbox && push(col.bbox, {
                        colIndex: i,
                        label: col.text ?? ""
                    }));
                }
                // Removed catch-all else clause that was creating unwanted extra boxes
            });
            setSelectedAnnotations(flat);
            // 3) Give React a tick to render the new viewer before peeking
            // Allow viewer to mount highlights, then trigger peek + bump nonce
            setTimeout(() => triggerPeek(page, bbox), 0);
        } else {
            triggerPeek(page, bbox);
        }
    };

    const handleTableCellHover = (cellData) => {
        if (!cellData) {
            setHoveredKeyExternal(null);
            return;
        }
        const {page, bbox} = cellData;
        if (page && Array.isArray(bbox) && bbox.length === 4) {
            setHoveredKeyExternal(makeEntityKey(page, bbox));
        } else {
            setHoveredKeyExternal(null);
        }
    };

    function sniff(annots) {
        console.group("Viewer input");
        console.log("count:", annots?.length);
        console.log("first 3:", annots?.slice(0, 3));
        const shapeSummary = annots?.reduce((acc, a) => {
            Object.keys(a || {}).forEach(k => acc.add(k));
            return acc;
        }, new Set());
        console.log("top-level keys across items:", [...shapeSummary]);
        // bbox stats
        const bboxes = (annots || []).map(a => a?.bbox).filter(Boolean);
        if (bboxes.length) {
            const flat = bboxes.flat();
            const min = Math.min(...flat), max = Math.max(...flat);
            console.log("bbox value range:", {min, max});
        } else {
            console.warn("No top-level bbox found on items.");
        }
        console.groupEnd();
    }

    const handleConfirm = async () => {
        if (!editingCell) return;
        const {executionId, fileName, index} = editingCell;

        // Find the right result row by executionId (fallback to fileName)
        const res = executionId
            ? results.find(r => r?.data?.execution_id === executionId)
            : results.find(r => r.fileName === fileName);

        if (!res || res.status !== "success") return;

        // Rebuild headers exactly like the table does (prepend "Filename")
        const headers = JSON.parse(JSON.stringify(toolDetails?.headerConfig || []));
        if (!headers.length) return;
        headers.forEach((row, idx) => {
            if (idx === 0) row.unshift("Filename");
            else row.unshift("");
        });

        // Same helpers used elsewhere
        const leafHeaders = getLeafLabels(headers);
        const targetLabel = leafHeaders[index];
        const targetKey = norm(targetLabel);

        // Clone annotations and update the existing matching annotation in place so
        // redraw keeps the same page/bbox metadata after an inline edit.
        const annotations = JSON.parse(JSON.stringify(res.data.annotations || []));
        let updated = false;

        for (const a of annotations) {
            const k = keyFromAnnotation(a);
            if (k && (k === targetKey || k.includes(targetKey))) {
                if (a.type === "key_value") {
                    if (!a.value) a.value = {};
                    a.value.text = editValue;
                } else if (a.type === "entity" || a.type === "summary_row") {
                    a.text = editValue;
                }
                updated = true;
                break;
            }

            if (a?.type === "key_value_group" && Array.isArray(a.items)) {
                for (const item of a.items) {
                    const itemKey = norm(
                        (item?.field?.text) ??
                        (typeof item?.field === "string" ? item.field : "")
                    );
                    if (itemKey && (itemKey === targetKey || itemKey.includes(targetKey))) {
                        if (!item.value) item.value = {};
                        item.value.text = editValue;
                        updated = true;
                        break;
                    }
                }
                if (updated) break;
            }
        }

        if (!updated) {
            const fallback = {
                type: "entity",
                entity_type: targetLabel,
                label: targetLabel,
                text: editValue,
            };
            const existingOverride = overrides?.[executionId]?.[index];
            if (existingOverride?.page) fallback.page = existingOverride.page;
            if (existingOverride?.bbox) fallback.bbox = existingOverride.bbox;
            annotations.push(fallback);
        }

        // ✅ Update local edits keyed by executionId (fallback to fileName)
        const key = executionId ?? fileName;
        setEditedValues(prev => {
            const base = prev[key] ?? Array(leafHeaders.length).fill(undefined);
            const next = [...base];
            next[index] = editValue;
            return {...prev, [key]: next};
        });

        // Persist to backend using the execution id
        try {
            const execId = executionId ?? res?.data?.execution_id;
            if (execId) {
                const payload = {result_json: annotations};

                const resp = await apiFetch(`${API_BASE}/update_execution/${execId}`, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(payload),
                });

                if (!resp.ok) {
                    let msg = 'Failed to update execution';
                    try {
                        const err = await resp.json();
                        msg = err.error || msg;
                    } catch {
                        // ignore if not JSON
                    }
                    alert(`Failed: ${msg}`);
                } else {
                    // ✅ Keep local state in sync so subsequent edits start from the latest annotations
                    // (prevents later saves from reverting earlier edits)
                    if (res?.data) {
                        res.data.annotations = annotations;
                    }
                }
            }
        } catch (error) {
            console.error('Update error:', error);
            alert('Error updating execution.');
        }
        setEditingCell(null);
        setEditValue("");
    };


    const handleCancel = () => {
        setEditingCell(null);
        setEditValue("");
    };

    // Handle header editing
    const handleHeaderEdit = async (rowIndex, cellIndex, newValue, colSpan = 1) => {
        if (!toolDetails?.headerConfig) return;

        console.log('handleHeaderEdit called:', {rowIndex, cellIndex, newValue, colSpan});

        // Clone the headerConfig
        const updatedConfig = JSON.parse(JSON.stringify(toolDetails.headerConfig));
        console.log('Original headerConfig:', updatedConfig);

        // Map the cellIndex back to the original headerConfig
        // ResultTable adds 2 fixed columns (checkbox, filename) at the beginning
        // So we need to subtract 2 to get the actual column index in headerConfig
        const actualColIndex = cellIndex - 2;
        console.log('Mapped to actualColIndex:', actualColIndex);

        // Make sure we're not trying to edit a fixed column or out of bounds
        if (actualColIndex < 0 || actualColIndex >= updatedConfig[0]?.length) {
            console.warn('Invalid column index for header edit');
            return;
        }

        // Update only the cells covered by the colSpan of the clicked merged cell
        const mergeStart = actualColIndex;
        const mergeEnd = actualColIndex + colSpan - 1;

        console.log('Merge range from colSpan:', {mergeStart, mergeEnd, colSpan});

        // Check if the new value would cause unwanted merging with adjacent cells
        // (only check if we're updating a single cell, not an already-merged range)
        if (colSpan === 1) {
            const leftValue = actualColIndex > 0 ? updatedConfig[rowIndex][actualColIndex - 1] : null;
            const rightValue = actualColIndex < updatedConfig[rowIndex].length - 1 ? updatedConfig[rowIndex][actualColIndex + 1] : null;

            if (newValue && (newValue === leftValue || newValue === rightValue)) {
                const confirmMerge = window.confirm(
                    'The new value matches an adjacent column header, which will cause them to merge. Do you want to continue?'
                );
                if (!confirmMerge) {
                    return;
                }
            }
        }

        // Update the range of cells that belong to this merged cell
        for (let i = mergeStart; i <= mergeEnd; i++) {
            if (i < updatedConfig[rowIndex].length) {
                updatedConfig[rowIndex][i] = newValue;
            }
        }

        console.log('Updated headerConfig:', updatedConfig);

        try {
            // Update the backend
            const response = await apiFetch(`${API_BASE}/update_tool/${toolId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: toolDetails.name,
                    description: toolDetails.description,
                    headerConfig: updatedConfig
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Backend error:', errorData);
                throw new Error(errorData.error || 'Failed to update tool');
            }

            console.log('Backend update successful');

            // The toolDetails state is managed by useToolResults hook
            // We need to force a refresh - simplest way is to reload from the API
            // or we could update the hook to expose a setter
            window.location.reload();
        } catch (error) {
            console.error('Error updating header:', error);
            alert('Failed to update header. Please try again.');
        }
    };

    const closeViewer = useCallback(() => {
        setSelectedFile(null);
        setSelectedAnnotations([]);
        setSelectedExecutionId(null);
        setAllowEdits(false);
        setActiveRedraw(null);
        setRemoveKeyExternal(null);
        setFocusPageExternal(null);
        setRedrawGroupId(null);
    }, [setSelectedFile, setSelectedAnnotations]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeViewer();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [closeViewer]);

    const exportResultsToExcel = (resultsToExport, outputFileName) => {
        if (!toolDetails?.headerConfig || !resultsToExport?.length) return;

        // Build 3 header rows like your tables: group / label / column
        const headerRows = 3;
        const header0 = [];
        const header1 = [];
        const header2 = [];

        // Prepend the fixed metadata column
        const filename = (t('historyTable.filename') ?? '').trim() || 'Filename';
        header0.push(filename);
        header1.push('');
        header2.push('');

        // Merge the metadata header vertically across all three header rows
        const merges = [{s: {r: 0, c: 0}, e: {r: headerRows - 1, c: 0}}];

        // Copy dynamic headers from headerConfig
        // headerConfig is an array of header rows; we append them to the right
        let colIndex = 1; // we already used col 0 for Filename
        const cfg = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));

        // Normalize to exactly 3 rows by spreading existing labels across header0/1/2
        const rows = cfg.length;
        const cols = cfg[0]?.length || 0;
        for (let c = 0; c < cols; c++) {
            // find “top/middle/bottom” semantics similar to your tables:
            // - top: group, middle: block label, bottom: leaf label
            const top = rows > 0 ? (cfg[0][c] || '') : '';
            const mid = rows > 1 ? (cfg[1][c] || '') : '';
            const bottom = rows > 2 ? (cfg[2][c] || '') : (rows > 1 ? mid : top);

            // Heuristic similar to your history exporter:
            // if only one label present, merge it down; if two, merge across remaining row.
            if (rows >= 3) {
                header0.push(top);
                header1.push(mid);
                header2.push(bottom);
            } else if (rows === 2) {
                header0.push(top);
                header1.push(bottom);
                header2.push('');
                merges.push({s: {r: 1, c: colIndex}, e: {r: 2, c: colIndex}});
            } else {
                header0.push(top);
                header1.push('');
                header2.push('');
                merges.push({s: {r: 0, c: colIndex}, e: {r: 2, c: colIndex}});
            }
            colIndex += 1;
        }

        const ws_data = [header0, header1, header2];

        // Build body rows: one row per result
        resultsToExport.forEach((r) => {
            const row = new Array(colIndex).fill('');
            row[0] = r?.fileName ?? '';
            // Fill values for dynamic columns from leaf labels
            const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig || []));
            headers.forEach((rowArr, idx) => {
                if (idx === 0) rowArr.unshift('Filename'); else rowArr.unshift('');
            }); // match table shape
            const leaf = (function getLeafLabels(hdr) {
                if (!hdr?.length) return [];
                const hRows = hdr.length;
                const hCols = hdr[0].length;
                const out = [];
                for (let c = 1; c < hCols; c++) {
                    let label = '';
                    for (let r = hRows - 1; r >= 0; r--) {
                        const v = hdr[r][c];
                        if (v && String(v).trim()) {
                            label = String(v);
                            break;
                        }
                    }
                    if (!label) {
                        for (let r = 0; r < hRows; r++) {
                            const v = hdr[r][c];
                            if (v && String(v).trim()) {
                                label = String(v);
                                break;
                            }
                        }
                    }
                    out.push(label);
                }
                return out;
            })(headers);

            // Map annotations -> value by first-match (like your table)
            const firstMatch = new Map();
            (r?.data?.annotations || []).forEach((a) => {
                const norm = (s) => (s ?? '').toString().trim().toLowerCase().replace(/[：:]+$/g, '');
                const keyFromAnnotation = (ann) => {
                    if (!ann?.type) return null;
                    if (ann.type === 'key_value') {
                        const ft = ann.field_text;
                        if (typeof ft === 'string' && ft.trim()) return norm(ft);
                        const f = ann.field;
                        if (typeof f === 'string' && f.trim()) return norm(f);
                        if (f && typeof f === 'object') {
                            const t = f.text || f.label || '';
                            if (t && t.trim()) return norm(t);
                        }
                        return null;
                    }
                    if (ann.type === 'entity' || ann.type === 'summary_row') {
                        const et = ann.entity_type || ann.label;
                        if (typeof et === 'string' && et.trim()) return norm(et);
                        return null;
                    }
                    return null;
                };
                const k = keyFromAnnotation(a);
                if (k && !firstMatch.has(k)) {
                    if (a.type === 'key_value') firstMatch.set(k, a.value?.text || '');
                    else if (a.type === 'entity' || a.type === 'summary_row') firstMatch.set(k, a.text || '');
                }
            });

            // Write values to columns 1..N
            leaf.forEach((label, i) => {
                const norm = (s) => (s ?? '').toString().trim().toLowerCase().replace(/[：:]+$/g, '');
                const target = norm(label);
                let val = '';
                if (target) {
                    if (firstMatch.has(target)) {
                        val = firstMatch.get(target);
                    } else {
                        for (const [k, v] of firstMatch.entries()) {
                            if (k.includes(target)) {
                                val = v;
                                break;
                            }
                        }
                    }
                }
                row[1 + i] = val ?? '';
            });

            // 🔁 Overlay any inline edits so export matches what the user sees
            const execId = r?.data?.execution_id ?? r?.fileName;
            const localEdits = editedValues?.[execId];
            if (Array.isArray(localEdits)) {
                for (let i = 0; i < leaf.length; i++) {
                    const edited = localEdits[i];
                    if (edited !== undefined && edited !== null) {
                        row[1 + i] = edited;
                    }
                }
            }

            ws_data.push(row);
        });


        // Create sheet and apply merges/styles
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        ws['!merges'] = merges;

        // Column widths
        ws['!cols'] = ws_data[0].map((_, cIdx) => {
            const colTexts = ws_data.map(r => r[cIdx] ?? '');
            const maxLen = Math.max(...colTexts.map(v => v?.toString?.().length ?? 0));
            return {wch: Math.min(Math.max(10, (maxLen || 0) + 2), 60)};
        });

        // Header style — centered, bold, grey, boxed
        const centerBoldHeader = {
            alignment: {horizontal: 'center', vertical: 'center'},
            font: {bold: true},
            border: {
                top: {style: 'thin', color: {rgb: '000000'}},
                bottom: {style: 'thin', color: {rgb: '000000'}},
                left: {style: 'thin', color: {rgb: '000000'}},
                right: {style: 'thin', color: {rgb: '000000'}}
            },
            fill: {fgColor: {rgb: 'E0E0E0'}}
        };
        for (let c = 0; c < colIndex; c++) {
            for (let r = 0; r < headerRows; r++) {
                const addr = XLSX.utils.encode_cell({r, c});
                if (ws[addr]) ws[addr].s = centerBoldHeader;
            }
        }

        // Borders: solid vertical separators for the metadata col (c=0),
        // hairline inside everything else, with thin left/right walls around data block.
        for (let r = headerRows; r < ws_data.length; r++) {
            for (let c = 0; c < colIndex; c++) {
                const addr = XLSX.utils.encode_cell({r, c});
                const isFilenameCol = (c === 0);
                const isStartOfData = (c === 1);            // left wall of dynamic data
                const isEndOfSheet = (c === colIndex - 1); // right wall

                const border = {
                    top: {style: 'hair', color: {rgb: '999999'}},
                    bottom: {style: 'hair', color: {rgb: '999999'}},
                    left: {style: 'none'},
                    right: {style: 'none'}
                };

                if (isFilenameCol) {
                    border.left = {style: 'thin', color: {rgb: '000000'}};
                    border.right = {style: 'thin', color: {rgb: '000000'}};
                } else {
                    if (isStartOfData) border.left = {style: 'thin', color: {rgb: '000000'}};
                    if (isEndOfSheet) border.right = {style: 'thin', color: {rgb: '000000'}};
                }

                const style = {alignment: {horizontal: 'left', vertical: 'center'}, border};
                if (ws[addr]) ws[addr].s = {...(ws[addr].s || {}), ...style};
                else ws[addr] = {v: ws_data[r][c], s: style};
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Results');
        XLSX.writeFile(wb, outputFileName);
    };

    const exportCurrentPageToExcel = () => {
        const resultsForPage = visiblePairs.map(p => p.res);
        exportResultsToExcel(resultsForPage, `results_page_${page}.xlsx`);
    };

    const exportAllToExcel = () => {
        // Use alivePairs to respect any deletions that have occurred
        const allResults = alivePairs.map(p => p.res);
        exportResultsToExcel(allResults, 'results_all.xlsx');
    };

    const exportSelectedToExcel = () => {
        const selectedResults = alivePairs
            .filter(p => selectedIds.has(p.res?.data?.execution_id))
            .map(p => p.res);
        if (selectedResults.length > 0) {
            exportResultsToExcel(selectedResults, 'results_selected.xlsx');
        } else {
            // This case should be rare since the button is disabled, but it's good practice.
            alert(t('nothing_to_export') || 'Nothing to export.');
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            sx={{height: '100dvh', overflow: 'hidden'}}
        > <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            mb={{xs: 3, sm: 4}}
            pt={4} pl={2} pr={3}
        >
            <Box>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/home')}
                >
                    {t('homepage')}
                </Button>
            </Box>
            <Box sx={{flex: 1, minWidth: 0, mx: 2, textAlign: 'center'}}>
                <Box display="inline-flex" alignItems="center" gap={1}>
                    <Typography variant="h4">{t("processing_results")}</Typography>

                    {toolId && (
                        <Button
                            variant="text"
                            size="medium"
                            sx={{textTransform: 'none', ml: 1}}
                            onClick={() =>
                                navigate(`/tool/${toolId}/history`, {
                                    state: {toolName: toolDetails?.name || undefined},
                                })
                            }
                        >
                            ({t("history")})
                        </Button>
                    )}
                </Box>
            </Box>
            <Box>
                <LanguageThemeBar/>
            </Box>
        </Box>

            <Box
                display="flex"
                width="100%"
                flex="1 1 auto"
                alignItems="stretch"
                sx={{minHeight: 0, pl: 2, pr: 3, pb: 4}}
            >
                <Box
                    width={selectedFile ? `calc(100% - ${viewerWidthPct}% - 16px)` : '100%'}
                    height="100%"
                >

                    <ResultTable
                        files={visiblePairs.map(p => ({file: p.file?.file || p.file}))}
                        results={visiblePairs.map(p => p.res)}
                        toolDetails={toolDetails}
                        editedValues={editedValues}
                        editingCell={editingCell}
                        setEditingCell={setEditingCell}
                        editValue={editValue}
                        setEditValue={setEditValue}
                        handleConfirm={handleConfirm}
                        handleCancel={handleCancel}
                        theme={theme}
                        compact
                        selectedExecutionId={selectedExecutionId}
                        onCellClick={handleTableCellClick}
                        onCellHover={handleTableCellHover}
                        hoveredKeyExternal={hoveredKeyExternal}
                        onCellRedraw={handleCellRedraw}
                        extractingCell={extractingCell}
                        processingCell={processingCell}
                        overrides={overrides}
                        leftExtra={bulkActions}
                        onToggleRow={toggleRowSelected}
                        selectedIds={selectedIds}
                        deletedIds={deletedIds}
                        page={page}
                        hasNext={hasNext}
                        hasPrev={hasPrev}
                        onNext={() => setPage(p => Math.min(p + 1, Math.max(1, Math.ceil(totalRows / pageSize))))}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        total={totalRows}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setPage(1);
                        }}
                        onCellDoubleClick={({executionId, fileName, index, value}) => {
                            setEditingCell({executionId, fileName, index});
                            setEditValue(value ?? "");
                        }}
                        onHeaderEdit={handleHeaderEdit}
                        onFilenameClick={(executionId, annotations, fileObj) => {
                            setAllowEdits(false);
                            setActiveRedraw(null);
                            setRemoveKeyExternal(null);
                            setFocusPageExternal(null);
                            setRedrawGroupId(null);
                            sniff(annotations);
                            const flat = [];
                            (annotations || []).forEach(a => {
                                // most viewers are 0-based; your data shows page: 1
                                const page = (a.page ?? 1);
                                const push = (bbox, extra = {}) => {
                                    if (Array.isArray(bbox) && bbox.length === 4) {
                                        // Normalize as 'entity' so getAnnotationKey => `${page}_entity_${bbox}`
                                        const uid = `${page}_entity_${bbox.join('_')}`;
                                        flat.push({
                                            page,
                                            bbox,
                                            type: 'entity',
                                            __uid: uid,
                                            annotationId: a.id,
                                            ...extra
                                        });
                                    }
                                };
                                if (a.type === "key_value") {
                                    if (a.field?.bbox) push(a.field.bbox, {label: a.field?.text ?? ""});
                                    if (a.value?.bbox) push(a.value.bbox, {label: a.value?.text ?? ""});
                                } else if (a.type === "key_value_group" && Array.isArray(a.items)) {
                                    const pageNum2 = a.page ?? a.source?.page ?? page;
                                    const groupBox = (a.source && Array.isArray(a.source.bbox) ? a.source.bbox : a.bbox);
                                    const groupId = a.group_id || `${pageNum2}_group_${a.group_label || "group"}_${Array.isArray(groupBox) ? groupBox.join('_') : 'nogb'}`;
                                    for (const item of a.items) {
                                        const vb = item?.value?.bbox;
                                        if (Array.isArray(vb) && vb.length === 4) {
                                            const uid2 = `${pageNum2}_entity_${vb.join('_')}`;
                                            flat.push({
                                                page: pageNum2,
                                                bbox: vb,
                                                type: 'entity',
                                                __uid: uid2,
                                                label: item?.field?.text ?? "",
                                                groupId,
                                                annotationId: a.id
                                            });
                                        }
                                    }
                                } else if (a.type === "entity" || a.type === "summary_row") {
                                    if (a.bbox) push(a.bbox, {label: a.label ?? a.entity_type ?? ""});
                                } else if (a.type === "table_row" && Array.isArray(a.columns)) {
                                    a.columns.forEach((col, i) => col?.bbox && push(col.bbox, {
                                        colIndex: i,
                                        label: col.text ?? ""
                                    }));
                                }
                                // Removed catch-all else clause that was creating unwanted extra boxes
                            });
                            setSelectedAnnotations(flat);
                            setSelectedFile(fileObj);
                            setSelectedExecutionId(executionId);
                        }
                        }
                        rightExtra={
                            <Button
                                size="small"
                                variant="contained"
                                endIcon={<ArrowDropDownRoundedIcon/>}
                                onClick={openExportMenu}
                                disabled={!toolDetails?.headerConfig || !results?.length}
                            >
                                {t('export_to_excel')}
                            </Button>
                        }
                    />
                </Box>


                {selectedFile && (
                    <Box sx={{
                        position: 'fixed',
                        top: 100,
                        right: 10,
                        bottom: 0,
                        width: `${viewerWidthPct}%`,
                        bgcolor: 'background.paper',
                        zIndex: 1200
                    }}>

                        {/* big obvious close control */}
                        <Fab
                            variant="extended"
                            color="primary"
                            size="medium"
                            onClick={closeViewer}
                            aria-label="Close viewer"
                            sx={(theme) => ({
                                position: 'absolute',
                                zIndex: 2500,
                                top: `max(${theme.spacing(1)}, env(safe-area-inset-top))`,
                                right: `max(${theme.spacing(1)}, env(safe-area-inset-right))`,
                                textTransform: 'none',
                                height: 30,
                                borderRadius: 999,
                                px: 1.75,
                                boxShadow: 'none',
                                backgroundColor: alpha(theme.palette.primary.main, 0.8), // 👈 80% opaque
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.9), // slightly less transparent on hover
                                    boxShadow: 'none',
                                },
                            })}
                        >

                            <CloseRoundedIcon sx={{mr: 1}}/>
                            {t("close")}
                        </Fab>
                        <VerticalResizeHandle
                            ariaLabel="Resize table/viewer"
                            rightCss={`calc(${viewerWidthPct}% - 3px)`}
                            onDrag={(dx) => {
                                const winW = window.innerWidth || 1;
                                const deltaPct = (dx / winW) * 100;
                                setViewerWidthPct((p) => clamp(p - deltaPct, 35, 65));
                            }}
                        />
                        <ViewerContainer
                            key={`${selectedExecutionId}_${viewerKey}`}  // Force remount when switching documents or reloading annotations
                            showHeader={false}
                            selectedAnnotations={selectedAnnotations}
                            setSelectedAnnotations={setSelectedAnnotations}
                            defaultGroupIdForNewAnns={redrawGroupId}
                            groupMeta={{}}
                            setGroupMeta={() => {
                            }}
                            file_input={selectedFile}
                            hideSelectionPanel
                            readOnlyHighlights={false}
                            forceAllSelected
                            hideToolbar={false}
                            showZoomControls
                            showAnnotateControls={false}
                            preventAutoExtract
                            peekKeyExternal={peekKeyExternal}
                            peekNonceExternal={peekNonce}
                            hoveredKeyExternal={hoveredKeyExternal}
                            onBoxHover={setHoveredKeyExternal}
                            // 🔧 redraw signals
                            removeKeyExternal={removeKeyExternal}
                            removeNonceExternal={removeNonce}
                            focusPageExternal={focusPageExternal}
                            onAnnotationAdded={handleAnnotationAdded}
                            onAnnotationMoved={handleAnnotationMoved}
                            disableReprocessOnDrag={true}
                            containerWidth="100%"
                            paperSx={{maxWidth: 'none', mt: 0}}   // optional: let it expand fully
                            containerSx={{mt: 0, pt: 0}}
                            // containerSx={{ p: 1 }}        // optional: add internal padding if desired
                        />
                    </Box>
                )}
            </Box>

            {/* Delete confirmation dialog */}
            <ConfirmDeleteDialog
                open={deleteOpen}
                count={selectedIds.size}
                loading={deleting}
                onCancel={() => !deleting && setDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
            />

            {/* The Menu is placed here, outside the table's stacking context */}
            <Menu
                open={Boolean(exportMenuPosition)}
                onClose={closeExportMenu}
                anchorReference="anchorPosition"
                anchorPosition={exportMenuPosition}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem
                    onClick={() => {
                        closeExportMenu();
                        exportCurrentPageToExcel();
                    }}
                >
                    <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                    <ListItemText primary={t('export_current_page') || 'Export current page'}/>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        closeExportMenu();
                        exportAllToExcel();
                    }}
                >
                    <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                    <ListItemText primary={t('export_all') || 'Export all'}/>
                </MenuItem>
                <MenuItem
                    disabled={selectedIds.size === 0}
                    onClick={() => {
                        closeExportMenu();
                        exportSelectedToExcel();
                    }}
                >
                    <ListItemIcon><FileDownloadOutlinedIcon fontSize="small"/></ListItemIcon>
                    <ListItemText primary={t('export_selected_rows') || 'Export selected rows'}/>
                </MenuItem>
            </Menu>
        </Box>


    );
}
